import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, inject, OnInit, ViewChild } from '@angular/core';
import { PiasLayoutComponent } from '../../shared/components/pias-layout/pias-layout.component';
import { SessionStorageService } from '../../core/services/session-storage/session-storage.service';
import { SnackbarService } from '../../shared/services/snackbar/snackbar.service';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { SelectOptions } from '../../core/interfaces/select-options.interface';
import { MatDividerModule } from '@angular/material/divider';
import { DownloadResourcesService } from '../../core/services/download-resources/download-resources.service';
import { GetDownloadResourceListModel } from '../../core/models/get-download-resource-list/get-download-resource-list.model';
import { HttpStatusCode } from 'axios';
import { DownloadResourceList } from '../../core/interfaces/download-resource-list.interface';
import { MatPaginator, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatIconModule } from '@angular/material/icon';
import { UserLogService } from '../../core/services/user-log/user-log.service';
import { Router } from '@angular/router';
import { ConstantsService } from '../../core/services/constants/constants.service';

@Component({
  selector: 'app-download-resources',
  imports: [
    CommonModule,
    PiasLayoutComponent,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDividerModule,
    MatPaginatorModule,
    MatIconModule
  ],
  templateUrl: './download-resources.page.html',
  styleUrl: './download-resources.page.scss'
})
export class DownloadResourcesPage implements OnInit, AfterViewInit {

  private router = inject(Router);
  private formBuilder = inject(FormBuilder);
  private sessionStorageService = inject(SessionStorageService);
  private snackBarService = inject(SnackbarService);
  private downloadResourcesService = inject(DownloadResourcesService);
  private userLogService = inject(UserLogService);
  private constantsService = inject(ConstantsService);

  protected downloadResourceCategoryOptions: SelectOptions[] = [];
  protected getDownloadResourceListModel = new GetDownloadResourceListModel();
  protected downloadResourceListDataSource: DownloadResourceList[] = [];
  protected downloadResourceList: DownloadResourceList[] = [];
  protected tempDownloadResourceList: DownloadResourceList[] = [];
  protected downloadResourcePageSize: number = 10;
  protected downloadResourceCurrentPage: number = 0;
  protected downloadResourceLength: number = 0;

  @ViewChild(MatPaginator)
  downloadResourcePaginator!: MatPaginator;

  downloadResourceFg!: FormGroup;

  constructor() { }

  async ngOnInit(): Promise<void> {
    this.downloadResourceFg = this.formBuilder.group({
      searchKeyword: [''],
      drCategory: ['']
    });

    await this.getdownloadResourceCategoryList();

    await this.getDownloadResourceList();

    this.__applyDownloadResourceFilter();
  }

  ngAfterViewInit(): void {
  }

  async getdownloadResourceCategoryList() {
    const responseData = await this.constantsService
      .getConstantsList()
      .then((response) => {
        return JSON.parse(response);
      })
      .catch((error) => {
        console.error(error);
        return error;
      });

    const categoryList: any[] = responseData.data.responsedata.categories;

    this.downloadResourceCategoryOptions = [];
    categoryList.forEach((category) => {
      this.downloadResourceCategoryOptions.push({
        name: category,
        value: category
      });
    });
  }

  async getDownloadResourceList() {
    this.getDownloadResourceListModel.setUseremail(this.sessionStorageService.getCurrentUserEmail());
    const responseData = await this.downloadResourcesService
      .getDownloadResourceList(this.getDownloadResourceListModel)
      .then((response) => {
        return JSON.parse(response);
      })
      .catch((error) => {
        console.error(error);
        return error;
      });

    // Handling the response from the API call.
    if (responseData.status === HttpStatusCode.Ok) {
      this.downloadResourceList = responseData.data.responsedata;
    } else {
      if (responseData.code === HttpStatusCode.BadRequest) {
        // Display notification on API failure.
        this.snackBarService.openFailureSnackBar("Something went wrong!!!");
      }
      if (responseData.code === HttpStatusCode.Unauthorized) {
        this.snackBarService.openFailureSnackBar("Session timed out. Try logging in again!");
        this.router.navigate(['/login']);
      }

      this.sessionStorageService.clearSessionStorage();
    }
  }

  protected __applyDownloadResourceFilter() {
    let searchKeyword: string = this.downloadResourceFg.get('searchKeyword')?.value.toString();
    let drCategory: string = this.downloadResourceFg.get('drCategory')?.value.toString();

    if (searchKeyword && !drCategory) {
      this.tempDownloadResourceList = this.downloadResourceList.filter(
        drItem => drItem.resourceFormatName.toLowerCase().includes(searchKeyword.toLowerCase())
          || drItem.resourceTitle.toLowerCase().includes(searchKeyword.toLowerCase())
      );
    }
    else if (!searchKeyword && drCategory) {
      this.tempDownloadResourceList = this.downloadResourceList.filter(
        drItem => drItem.resourceCategoryName.toLowerCase() === drCategory.toLowerCase()
      );
    }
    else if (searchKeyword && drCategory) {
      this.tempDownloadResourceList = this.downloadResourceList.filter(
        drItem => (drItem.resourceFormatName.toLowerCase().includes(searchKeyword.toLowerCase())
          || drItem.resourceTitle.toLowerCase().includes(searchKeyword.toLowerCase()))
          && drItem.resourceCategoryName.toLowerCase().toString() === drCategory.toLowerCase().toString()
      );
    }
    else {
      this.tempDownloadResourceList = this.downloadResourceList;
    }

    this.__downloadResourceUpdatePageItems();
  }

  protected __downloadResourceUpdatePageItems() {
    const startIndex: number = this.downloadResourceCurrentPage * this.downloadResourcePageSize;
    const endIndex: number = startIndex + this.downloadResourcePageSize;
    this.downloadResourceLength = this.tempDownloadResourceList.length;

    if (this.downloadResourceLength < 5) {
      this.downloadResourcePaginator.disabled = true;
    }
    else {
      this.downloadResourcePaginator.disabled = false;
    }

    this.downloadResourceListDataSource = this.tempDownloadResourceList.slice(startIndex, endIndex);
  }

  protected __downloadResourceOnPageChange() {
    this.downloadResourcePageSize = this.downloadResourcePaginator.pageSize;
    this.downloadResourceCurrentPage = this.downloadResourcePaginator.pageIndex;
    this.__downloadResourceUpdatePageItems();
  }

  protected __getFileType(resourceLink: string) {
    let fileUrlName: string[] = resourceLink.split(".");
    return fileUrlName[fileUrlName.length - 1];
  }

  protected async __userLogDocument(resourceLink: string) {
    let fileUrlName: string[] = resourceLink.split("/");
    await this.downloadResourcesService.downloadResourceFile(resourceLink, fileUrlName[fileUrlName.length - 1]);
    await this.userLogService.logUserDownloadResource(fileUrlName[fileUrlName.length - 1]);
  }

  protected clearSearch() {
    this.downloadResourceFg.get('searchKeyword')?.setValue('');
    this.__applyDownloadResourceFilter();
  }

  protected async __printDownloadResource(resourceLink: string) {
    await this.downloadResourcesService.printPdf(resourceLink);
  }


}


